import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "react-toastify";

// reducers
import { fetchSupportedLocations } from '../stores/locations';

const PAGE_SIZE = 10; 

/**
 * Groups a flat array of locations by state.
 *
 * Input:  [{ state: 'Kedah', municipality: 'Sungai Petani', ... }, ...]
 * Output: { 'Kedah': [{...}, {...}], 'Penang': [{...}] }
 *
 * Used to render the grouped table layout where state appears once with a rowSpan.
 */
const groupByState = (locations) => {
  const grouped = {};
  for (const loc of locations) {
    if (!grouped[loc.state]) {
      grouped[loc.state] = [];
    }
    grouped[loc.state].push(loc);
  }
  // Sort states alphabetically
  const sorted = {};
  Object.keys(grouped).sort().forEach(key => {
    sorted[key] = grouped[key];
  });
  return sorted;
};

/**
 * TableSupportedLocations
 *
 * Props:
 *   - selectedState: string — filter value from dropdown ('' = all states)
 *
 * HOW IT WORKS:
 * When selectedState changes (or on mount), dispatches fetchSupportedLocations
 * with the state filter. This queries DynamoDB via the country-index GSI.
 * A cancellation flag prevents stale responses from overwriting newer data.
 *
 * If selectedState is '' (All States), fetches all locations for the country.
 * If a specific state is selected, queries only that state.
 */

export const TableSupportedLocations = ({ selectedState = '' }) => {

  // hooks
  const dispatch = useDispatch();
  const { lists = [], loading = false, initialized = false } = useSelector(context => context.locations);
  const [page, setPage] = useState(1);

  /* // ===== FETCH ONCE ON MOUNT =====
  // Only call the Lambda if we haven't loaded data yet.
  // This prevents re-fetching every time the user navigates away and back.
  useEffect(() => {
    if (!initialized) {
      const loadData = async () => {
        try {
          await dispatch(fetchSupportedLocations()).unwrap();
          console.log('tables.supported.locations@loadData', 'initial fetch complete');
        } catch (err) {
          console.error('tables.supported.locations@loadData', err);
          toast.error('Failed to load supported locations');
        }
      };
      loadData();
    }
  }, [initialized, dispatch]);

  // ===== FILTER CLIENT-SIDE WHEN DROPDOWN CHANGES =====
  // This dispatches a synchronous Redux action — no API call.
  // The reducer filters allLocations → lists based on selectedState.
  useEffect(() => {
    if (initialized) {
      dispatch(filterByState(selectedState));
    }
  }, [selectedState, initialized, dispatch]); */

  // ===== FETCH FROM API WHEN FILTER CHANGES =====
useEffect(() => {
  let cancelled = false;
  const loadData = async () => {
    try {
      const result = await dispatch(fetchSupportedLocations(
        selectedState ? { state: selectedState } : undefined
      )).unwrap();
      // If this effect was superseded by a newer one, discard the result
      if (cancelled) return;
    } catch (err) {
      if (cancelled) return;
      console.error('tables.supported.locations@loadData', err);
      toast.error('Failed to load supported locations');
    }
  };
  loadData();
  return () => { cancelled = true; };
}, [selectedState, dispatch]);

  // reset to first page whenever the state filter changes
  useEffect(() => { setPage(1); }, [selectedState]);

  // Group the flat list by state for display
  const grouped = groupByState(lists);
  const allStateKeys = Object.keys(grouped);

  // client-side pagination over STATE GROUPS (state data is small; one fetch, cached)
  const totalPages = Math.max(1, Math.ceil(allStateKeys.length / PAGE_SIZE));
  const stateKeys = allStateKeys.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="mt-8">
      <div className="mt-4 rounded-md shadow-md overflow-hidden">

        {/* Loading state */}
        {loading && (
          <div className="p-8 flex items-center justify-center bg-white">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <p className="ml-3 text-sm text-gray-500">Loading locations...</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && stateKeys.length === 0 && (
          <div className="p-8 flex flex-col items-center justify-center bg-white">
            <p className="text-sm font-bold text-gray-500">No data</p>
            <p className="text-sm text-gray-500">
              {selectedState
                ? `No supported locations found for "${selectedState}".`
                : 'No supported locations found.'}
            </p>
          </div>
        )}


        {/* Table with grouped rows */}
        {!loading && stateKeys.length > 0 && (
          <table className="min-w-full">
            <thead className="border-y border-slate-200 bg-slate-100">
              <tr>
                <th className="py-4 pl-4 text-left text-sm font-semibold text-slate-800">State</th>
                <th className="py-4 text-left text-sm font-semibold text-slate-800">Municipality</th>
                { /*<th className="py-4 text-left text-sm font-semibold text-slate-800">Post Code</th>*/}
                <th className="py-4 text-left text-sm font-semibold text-slate-800">Country</th>
                <th className="py-4 text-left text-sm font-semibold text-slate-800">Country Code</th>
                <th className="py-4 text-left text-sm font-semibold text-slate-800">Label</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {stateKeys.map((stateName) => {
                const municipalities = grouped[stateName];
                return municipalities.map((loc, idx) => (
                  <tr key={`${stateName}-${idx}`} className="hover:bg-gray-50">
                    {/* STATE COLUMN with rowSpan — only renders on first row of each group */}
                    {idx === 0 && (
                      <td
                        rowSpan={municipalities.length}
                        className="py-4 pl-4 align-top border-r border-gray-100"
                      >
                        <p className="text-sm text-slate-800 font-semibold">{stateName}</p>
                        <p className="text-xs text-gray-400 mt-1">{municipalities.length} location{municipalities.length > 1 ? 's' : ''}</p>
                      </td>
                    )}
                    {/* MUNICIPALITY */}
                    <td className="py-3 pl-2">
                      <p className="text-sm text-slate-600">{loc.municipality}</p>
                    </td>
                    {/* POST CODE */}
                    { /* <td className="py-3">
                      <p className="text-sm text-slate-600">{loc.postal_code}</p>
                    </td> */ }
                    {/* COUNTRY */}
                    <td className="py-3">
                      <p className="text-sm text-slate-600">{loc.country === 'MYS' ? 'Malaysia' : loc.country}</p>
                    </td>
                    {/* COUNTRY CODE — badge style */}
                    <td className="py-3">
                      <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                        {loc.country_code}
                      </span>
                    </td>
                    {/* LABEL */}
                    <td className="py-3">
                      <p className="text-sm text-gray-500">{loc.label}</p>
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* client-side pagination — matches Articles / Media Guides */}
      {!loading && totalPages > 1 && (
        <div className="mt-4 flex justify-end items-center gap-2 text-sm">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-50">Prev</button>
          <span className="text-gray-500">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-50">Next</button>
        </div>
      )}
    </div>
  );
};